import React, { memo, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { UseFormRegister, UseFormSetValue, FieldValues, Path, PathValue } from "react-hook-form";
import { useDropzone, FileRejection } from "react-dropzone";
import {
  getApiBaseUrl,
  getSecureCookie,
  extractS3KeyFromUrl,
  getContentTypeFromFileName,
  processJsonResponse,
  createBlobAndDownload,
} from "@ippon/shared-utils";
import "./CustomFileUploader.css";
import CustomFileModal from "../Modal/CustomFileModal";
import { CustomPdfPreviewCanvas } from "../PDF/PreviewCanvas";
import { CustomFileNameDisplay } from "../../TruncateText/FileName/CustomFileNameDisplay";

export interface ImageData {
  data: string;
  originalFileName: string;
  mimeType: string;
  size?: number;
}

export const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const convertFileToImageData = async (
  file: File
): Promise<ImageData> => {
  const dataUrl = await readFileAsBase64(file);
  return {
    data: dataUrl,
    originalFileName: file.name,
    mimeType: file.type,
    size: file.size,
  };
};

export const convertFilesToImageData = async (
  files: File[]
): Promise<ImageData[]> => {
  return Promise.all(files.map(convertFileToImageData));
};

const convertBlobUrlToBase64 = async (blobUrl: string): Promise<string> => {
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  } catch (error) {
    console.error("Blob URL変換エラー:", error);
    throw error;
  }
};

export const convertImageDataForBackend = (images: any[]): any[] => {
  return images.map((image, index) => {
    if (
      image &&
      typeof image === "object" &&
      (image.data || image.originalFileName || image.mimeType)
    ) {
      return image;
    }
    if (typeof image === "string") {
      return image;
    }
    if (image && typeof image === "object" && image.base64) {
      return {
        data: image.base64,
        originalFileName: image.name || `image_${index + 1}.jpg`,
        mimeType: "image/jpeg",
        size: image.size,
      };
    }
    return image;
  });
};

export const extractFileNameFromSignedUrl = (url: string): string => {
  if (!url || typeof url !== "string") return "image";
  const urlWithoutQuery = url.split("?")[0];
  let fileName = urlWithoutQuery.split("/").pop();
  if (!fileName) return "image";
  try {
    fileName = decodeURIComponent(fileName);
  } catch {
    // decodeURIComponentが失敗した場合は元のfileNameを使用
  }
  if (fileName.includes("-")) fileName = fileName.replace(/^\d+-/, "");
  return fileName;
};

interface ImageWithId {
  id: string;
  [key: string]: any;
}

export interface CustomFileUploaderProps<TFieldValues extends FieldValues = FieldValues> {
  initialImages?: string[] | string;
  type?: "multiple" | "single";
  showLabel?: string;
  name: Path<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  width?: string;
  onImagesDeleted?: (deletedImagePaths: string[]) => void;
  onImageChange?: () => void;
  isRequired?: boolean;
  label?: string;
  errors?: any;
  isReadOnly?: boolean;
  enablePanzoom?: boolean;
  rejectPdf?: boolean;
}

const generateUniqueId = (): string =>
  `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const addIdToImage = (image: any): ImageWithId => {
  if (image && typeof image === "object" && !image.id)
    return { ...image, id: generateUniqueId() };
  if (typeof image === "string")
    return { id: generateUniqueId(), data: image, isUrl: true };
  return image.id ? image : { ...image, id: generateUniqueId() };
};

const getImageSrc = (image: ImageWithId | any): string | any => {
  return (
    image?.public_file_path ||
    image?.data ||
    image?.base64 ||
    (image?.isUrl ? image.data : image)
  );
};

const isPdfFile = (image: ImageWithId): boolean => {
  const fileName = image?.originalFileName || image?.name || "";
  if (fileName.toLowerCase().endsWith(".pdf")) return true;
  const dataSource = getImageSrc(image);
  if (typeof dataSource === "string") {
    if (dataSource.startsWith("data:application/pdf")) return true;
    if (dataSource.toLowerCase().includes(".pdf")) return true;
  }
  if ((image as any)?.type === "application/pdf") return true;
  return false;
};

const CustomFileUploaderComponent = <TFieldValues extends FieldValues = FieldValues>({
  initialImages = [],
  type = "multiple",
  showLabel = "true",
  name,
  setValue,
  register,
  width = "180px",
  onImagesDeleted = () => {},
  onImageChange = () => {},
  isRequired = false,
  label = "",
  errors = {},
  isReadOnly = false,
  enablePanzoom = false,
  rejectPdf = false,
}: CustomFileUploaderProps<TFieldValues>) => {
    const [images, setImages] = useState<ImageWithId[]>([]);
    const [deletedImagePaths, setDeletedImagePaths] = useState<any>([]);
    const prevInitialImagesRef = useRef<any>(null);
    const hasUserAddedImagesRef = useRef<boolean>(false);
    const convertedBlobUrlsRef = useRef<Set<string>>(new Set());
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<{
      src: string;
      name?: string;
      isPdf?: boolean;
    } | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const onDrop = async (acceptedFiles: any) => {
      if (isReadOnly) return;
      setUploadError(null);
      
      if (rejectPdf) {
        const pdfFiles = acceptedFiles.filter((file: File) => 
          file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
        );
        if (pdfFiles.length > 0) {
          setUploadError(`PDFファイルはアップロードできません。JPEG、PNG、JPG形式のみ対応しています。`);
          setValue(name, (images.length > 0 ? convertImageDataForBackend(images) : []) as PathValue<TFieldValues, Path<TFieldValues>>, { 
            shouldValidate: true,
            shouldDirty: false 
          });
          return;
        }
      }
      
      try {
        const imageDataArray = await convertFilesToImageData(acceptedFiles);
        const imageDataWithIds = imageDataArray.map(addIdToImage);
        hasUserAddedImagesRef.current = true;
        if (type === "single") {
          setImages(imageDataWithIds);
          const formattedData = convertImageDataForBackend(imageDataWithIds);
          setValue(name, formattedData as PathValue<TFieldValues, Path<TFieldValues>>, { shouldDirty: true });
          onImageChange();
        } else {
          const newImages = [...images, ...imageDataWithIds];
          setImages(newImages);
          const formattedData = convertImageDataForBackend(newImages);
          setValue(name, formattedData as PathValue<TFieldValues, Path<TFieldValues>>, { shouldDirty: true });
          onImageChange();
        }
      } catch (error) {
        console.error("Error processing uploaded files:", error);
      }
    };
    
    const onDropRejected = (rejectedFiles: FileRejection[]) => {
      if (isReadOnly) return;
      const pdfFiles = rejectedFiles.filter((rejection) => 
        rejection.file.type === "application/pdf" || rejection.file.name.toLowerCase().endsWith(".pdf")
      );
      if (pdfFiles.length > 0 && rejectPdf) {
        setUploadError(`PDFファイルはアップロードできません。JPEG、PNG、JPG形式のみ対応しています。`);
      }
    };

    const handleDelete = (imageToDelete: ImageWithId) => {
      if (isReadOnly) return;
      const filteredImages = images.filter(
        (image: ImageWithId) => image.id !== imageToDelete.id
      );
      const deletedImageInfo = imageToDelete.isUrl
        ? imageToDelete.data
        : imageToDelete.data || imageToDelete.base64 || (imageToDelete as any);
      setDeletedImagePaths((prev: any) => [...prev, deletedImageInfo]);
      if (type !== "single") {
        setImages(filteredImages);
        setValue(name, convertImageDataForBackend(filteredImages) as PathValue<TFieldValues, Path<TFieldValues>>, { shouldDirty: true });
        onImageChange();
        if (filteredImages.length === 0) hasUserAddedImagesRef.current = false;
      } else {
        setImages([]);
        setValue(name, [] as PathValue<TFieldValues, Path<TFieldValues>>, { shouldDirty: true });
        setDeletedImagePaths([deletedImageInfo]);
        onImageChange();
        hasUserAddedImagesRef.current = false;
      }
    };

    const handleImageClick = (image: ImageWithId) => {
      const srcCandidate = getImageSrc(image);

      const imageName = image?.originalFileName
        ? image.originalFileName
        : image?.name
          ? image.name
          : image?.data
            ? extractFileNameFromSignedUrl(image?.data)
            : image?.base64 !== undefined
              ? extractFileNameFromSignedUrl(image?.base64)
              : extractFileNameFromSignedUrl(
                  image.isUrl ? image.data : (image as any)
                );

      setSelectedImage({
        src: srcCandidate,
        name: imageName,
        isPdf: isPdfFile(image),
      });
      setModalOpen(true);
    };

    const handleDownload = async (image: ImageWithId) => {
      try {
        const srcCandidate = getImageSrc(image);

        const imageName = image?.originalFileName
          ? image.originalFileName
          : image?.name
            ? image.name
            : image?.data
              ? extractFileNameFromSignedUrl(image?.data)
              : image?.base64 !== undefined
                ? extractFileNameFromSignedUrl(image?.base64)
                : extractFileNameFromSignedUrl(
                    image.isUrl ? image.data : (image as any)
                  );

        if (typeof srcCandidate !== "string") return;

        // Direct download for data URLs (base64)
        if (srcCandidate.startsWith("data:")) {
          const a = document.createElement("a");
          a.href = srcCandidate;
          a.download = imageName || "download";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }

        // Server-side download for remote URLs (S3 signed URLs)
        const apiBaseUrl = getApiBaseUrl();
        const token = getSecureCookie("token");

        // Extract S3 key from signed URL
        const s3Key = extractS3KeyFromUrl(srcCandidate);
        if (!s3Key) {
          console.error("Failed to extract S3 key from URL:", srcCandidate);
          return;
        }

        // Fetch file via server to avoid CORS issues and use authentication
        const response = await fetch(`${apiBaseUrl}/api/v1/download/file`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ key: s3Key }),
        });

        if (!response.ok) throw new Error("Failed to fetch file for download");

        const contentType =
          response.headers.get("content-type") ||
          getContentTypeFromFileName(imageName || "download");
        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        const dataToDownload = processJsonResponse(uint8Array) ?? uint8Array;
        createBlobAndDownload(
          dataToDownload,
          contentType,
          imageName || "download"
        );
      } catch (err) {
        console.error("Error downloading file:", err);
      }
    };

    const handleCloseModal = () => {
      setModalOpen(false);
      setSelectedImage(null);
    };

    useEffect(() => {
      if (deletedImagePaths.length > 0) onImagesDeleted(deletedImagePaths);
    }, [deletedImagePaths, onImagesDeleted]);

    useEffect(() => {
      const convertBlobUrls = async () => {
        const blobImages = images.filter((img) => {
          if (convertedBlobUrlsRef.current.has(img.id)) {
            return false;
          }
          const src = getImageSrc(img);
          return src && typeof src === "string" && src.startsWith("blob:");
        });

        if (blobImages.length > 0) {
          const convertedImages = await Promise.all(
            blobImages.map(async (img) => {
              const src = getImageSrc(img);
              if (src && typeof src === "string" && src.startsWith("blob:")) {
                try {
                  const base64 = await convertBlobUrlToBase64(src);
                  convertedBlobUrlsRef.current.add(img.id);
                  return { ...img, data: base64 };
                } catch (error) {
                  console.error("Blob URL変換失敗:", error);
                  return img;
                }
              }
              return img;
            })
          );

          const updatedImages = images.map((img) => {
            const converted = convertedImages.find((c) => c.id === img.id);
            return converted || img;
          });

          const hasChanges = updatedImages.some((img, idx) => {
            const originalSrc = getImageSrc(images[idx]);
            const updatedSrc = getImageSrc(img);
            return originalSrc !== updatedSrc;
          });

          if (hasChanges) {
            setImages(updatedImages);
            setValue(name, convertImageDataForBackend(updatedImages) as PathValue<TFieldValues, Path<TFieldValues>>, { shouldDirty: true });
          }
        }
      };

      convertBlobUrls();
    }, [images, name, setValue]);

    useEffect(() => {
      const isInitialImagesChanged =
        JSON.stringify(prevInitialImagesRef.current) !==
        JSON.stringify(initialImages);
      if (hasUserAddedImagesRef.current) {
        prevInitialImagesRef.current = initialImages;
        return;
      }
      if (isInitialImagesChanged) {
        if (Array.isArray(initialImages)) {
          if (initialImages.length > 0) {
            const imagesWithIds = initialImages.map(addIdToImage);
            setImages(imagesWithIds);
            setValue(name, convertImageDataForBackend(imagesWithIds) as PathValue<TFieldValues, Path<TFieldValues>>);
          } else {
            setImages([]);
            setValue(name, [] as PathValue<TFieldValues, Path<TFieldValues>>);
          }
        } else if (
          typeof initialImages === "string" &&
          initialImages.trim() !== ""
        ) {
          const imageWithId = addIdToImage(initialImages);
          setImages([imageWithId]);
          setValue(name, [initialImages] as PathValue<TFieldValues, Path<TFieldValues>>);
        } else if (
          initialImages === "" ||
          initialImages === null ||
          initialImages === undefined
        ) {
          setImages([]);
          setValue(name, [] as PathValue<TFieldValues, Path<TFieldValues>>);
        }
        prevInitialImagesRef.current = initialImages;
      }
    }, [initialImages, setValue, name]);

    useEffect(() => {
      register(name, {
        ...(isRequired && {
          required: `${label || name}は必須項目です`,
          validate: (value: any) => {
            if (Array.isArray(value) && value.length > 0) return true;
            if (!Array.isArray(value) && value) return true;
            return `${label || name}は必須項目です`;
          },
        }),
      });
    }, [register, name, isRequired, label]);

    const acceptTypes: { [key: string]: string[] } = { 
      "image/png": [], 
      "image/jpeg": [] 
    };
    if (!rejectPdf) {
      acceptTypes["application/pdf"] = [];
    }
    
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      onDropRejected,
      accept: acceptTypes,
      disabled: isReadOnly,
      noClick: isReadOnly,
      noDrag: isReadOnly,
    });

    const symbolOfAdditionForLargeImage = (
      <svg
        width="19"
        height="19"
        viewBox="0 0 15 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6.57814 7.80326H0.646484V6.25587H6.57814V0.324219H8.12552V6.25587H14.0572V7.80326H8.12552V13.7349H6.57814V7.80326Z"
          fill="#0B9DBD"
        />
      </svg>
    );

    const symbolOfAdditionForSmallImage = (
      <svg
        width="28"
        height="37"
        viewBox="0 0 28 37"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.8417 37V21.0277H0V16.0455H11.8417V0H16.1583V16.0455H28V21.0277H16.1583V37H11.8417Z"
          fill="#989898"
        />
      </svg>
    );

    return (
      <Box
        // AI 画面コンテキスト用 (getPageContext が拾う)
        data-field={name || undefined}
        data-label={label || undefined}
      >
        {label && (
          <Typography
            fontWeight={700}
            fontSize={12}
            color="#3e3e3e"
            sx={{
              mb: 1,
              mt: 0,
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
        )}
        <Box className="contentForUploadingImage">
          {Array.isArray(images) &&
            images?.map((image: ImageWithId, index) => {
              const uniqueKey = image.id;
              const isPdf = isPdfFile(image);
              const previewSrc = getImageSrc(image);
              return (
                <div
                  className="imageBlock"
                  key={uniqueKey}
                  style={{
                    width: width,
                    minWidth: width,
                    maxWidth: width,
                  }}
                >
                  {/*
                   * 削除 (×) ボタンが imageWrapper 右上から top:-5px ではみ出して
                   * 配置されるため、isReadOnly=false のときは上に 6px の余白を
                   * 確保してボタンの見切れを防ぐ。read-only 時 (支出 OCR の
                   * 「AI 抽出対象書類」等) はボタンを出さないので 0。
                   */}
                  <div
                    className="imageWrapper"
                    style={{ marginTop: isReadOnly ? 0 : 6 }}
                  >
                    {isPdf ? (
                      (() => {
                        const uploaderWidth = parseInt(width, 10) || 180;
                        const previewWidth = uploaderWidth - 10;
                        const previewHeight = Math.round(
                          previewWidth * (115 / 170)
                        );
                        return (
                          <CustomPdfPreviewCanvas
                            pdfUrl={previewSrc}
                            width={previewWidth}
                            height={previewHeight}
                            onClick={() => handleImageClick(image)}
                          />
                        );
                      })()
                    ) : (
                      <img
                        className="dragedImage"
                        src={previewSrc}
                        alt={`Selected ${index}`}
                        onClick={() => handleImageClick(image)}
                        style={{ cursor: "pointer" }}
                      />
                    )}
                    {!isReadOnly && (
                      <button
                        className="deleteButton"
                        onClick={() => handleDelete(image)}
                      />
                    )}
                  </div>
                  {showLabel === "true" && (
                    <div className="imageTitle">
                      <CustomFileNameDisplay
                        fileName={
                          image?.originalFileName
                            ? image.originalFileName
                            : image?.name
                              ? image.name
                              : image?.data
                                ? extractFileNameFromSignedUrl(image?.data)
                                : image?.base64 !== undefined
                                  ? extractFileNameFromSignedUrl(image?.base64)
                                  : extractFileNameFromSignedUrl(getImageSrc(image))
                        }
                        fontWeight={400}
                      />
                    </div>
                  )}
                  <button
                    className="downloadButton"
                    onClick={() => handleDownload(image)}
                    type="button"
                  >
                    ダウンロード
                  </button>
                </div>
              );
            })}
          {!isReadOnly && (type !== "single" || images.length === 0) ? (
            <Box
              className="containerForImageToUpload"
              sx={{
                width: width,
                minWidth: width,
                maxWidth: width,
              }}
            >
              <li
                {...getRootProps()}
                className={`areaToDropImage dropzone ${isDragActive ? "active" : ""}`}
              >
                <input {...getInputProps()} />
                {width === "131px" ||
                width === "180px" ||
                width === "328px" ||
                width === "354px"
                  ? symbolOfAdditionForLargeImage
                  : symbolOfAdditionForSmallImage}
                <p
                  className={`sentencesOfDropArea ${isDragActive ? "isDragActive" : ""}`}
                >
                  {width === "131px" ||
                  width === "180px" ||
                  width === "328px" ||
                  width === "354px" ? (
                    <>
                      ここにドラッグ＆ドロップ
                      <br />
                      <span className="sentencesOfDropAreaSpan">または</span>
                      <span className="imageButton">
                        <span>クリックして参照</span>
                      </span>
                    </>
                  ) : (
                    <span className="sentencesOfDropAreaSpan">
                      共有する<span className="spanWithBorder">書類</span>を探す
                    </span>
                  )}
                </p>
              </li>
            </Box>
          ) : null}
        </Box>
        {errors && (errors as any)[name] && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 0.5, display: "block" }}
          >
            {(errors as any)[name].message}
          </Typography>
        )}
        {uploadError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 0.5, display: "block" }}
          >
            {uploadError}
          </Typography>
        )}

        {selectedImage && (
          <CustomFileModal
            open={modalOpen}
            onClose={handleCloseModal}
            imageSrc={selectedImage.src}
            imageName={selectedImage.name}
            enablePanzoom={enablePanzoom}
          />
        )}
      </Box>
    );
};

CustomFileUploaderComponent.displayName = "CustomFileUploader";

const CustomFileUploader = memo(CustomFileUploaderComponent) as <TFieldValues extends FieldValues = FieldValues>(
  props: CustomFileUploaderProps<TFieldValues>
) => React.ReactElement;

export default CustomFileUploader;
